/*
 * @Description: 流向管理-上传流向（复用 mall / ai 现有接口）
 */
import ajax from './ajax';
import axios from 'axios';

const MALL_BASE = '/api/mall';
const AI_BASE = '/api/ai';
const UPLOAD_URL = `${MALL_BASE}/channelCmInvoice/uploadFiles`;
const OCR_URL = `${AI_BASE}/hlxj/sse/ocrInvoice`;

const uuidV4 = () => {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// 产品下拉字典（仅 flowManagerStatus=1 的产品）
const getProductDict = (name = '') =>
  ajax
    .Post({
      url: `${MALL_BASE}/product/findProductDictList`,
      data: {
        pageNumber: 1,
        pageSize: 50,
        name,
        isLike: true,
        flowManagerStatus: 1,
      },
      timeout: 20000,
    })
    .then(({ data }) => {
      if (!data || !data.success) return [];
      const list = data.data?.list || data.data || [];
      return Array.isArray(list) ? list : [];
    });

// 客户（经销商）列表，用于购买方名称匹配 cmId
const getCustomers = (cmName = '') =>
  ajax
    .Post({
      url: `${MALL_BASE}/channelCm/selectAllCmList`,
      data: { cmName },
      timeout: 20000,
    })
    .then(({ data }) => {
      if (!data || !data.success) return [];
      return Array.isArray(data.data) ? data.data : data.data?.list || [];
    });

// 附件上传：/api/mall/channelCmInvoice/uploadFiles，一次请求支持多文件，返回 url 数组
const uploadFiles = (files) => {
  const list = Array.isArray(files) ? files : [files];
  const formData = new FormData();
  list.forEach((file) => formData.append('file', file));
  return axios
    .post(UPLOAD_URL, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    })
    .then((res) => {
      const data = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
      if (!data || data.success === false) {
        throw new Error((data && data.message) || '文件上传失败');
      }
      const raw = data.data;
      const urls = (Array.isArray(raw) ? raw : [raw])
        .map((item) => {
          if (!item) return '';
          if (typeof item === 'string') return item;
          return item.url || item.fileUrl || item.urlPath || '';
        })
        .filter(Boolean);
      if (!urls.length) throw new Error('文件上传失败');
      return urls;
    });
};

// 解析发票识别结果（SSE 返回文本，可能带 markdown 代码块）
const parseInvoiceText = (text) => {
  if (!text) return [];
  let clean = String(text).replace(/```json|```/gi, '').trim();
  let data;
  try {
    data = JSON.parse(clean);
  } catch (e) {
    const indexes = ['{', '['].map((c) => clean.indexOf(c)).filter((i) => i >= 0);
    const start = indexes.length ? Math.min(...indexes) : -1;
    const end = Math.max(clean.lastIndexOf('}'), clean.lastIndexOf(']'));
    if (start < 0 || end <= start) return [];
    try {
      data = JSON.parse(clean.slice(start, end + 1));
    } catch (err) {
      return [];
    }
  }
  const invoices = Array.isArray(data) ? data : data?.invoices || data?.list || data?.data || [data];
  return (Array.isArray(invoices) ? invoices : [invoices])
    .filter(Boolean)
    .map((inv) => ({
      invoiceNo: inv.invoiceNo || inv.invoiceNumber || inv.number || inv.no || '',
      cmName: inv.cmName || inv.buyName || inv.customerName || inv.buyerName || '',
      invoiceDate: inv.invoiceDate || inv.date || '',
      totalAmount: inv.totalAmount ?? inv.amount ?? inv.totalMoney ?? '',
      productList: (inv.productList || inv.products || inv.items || inv.details || []).map((p) => ({
        productName: p.productName || p.name || p.itemName || '',
        specify: p.specify || p.specification || p.spec || p.model || '',
        unit: p.unit || '',
        num: p.num ?? p.quantity ?? p.number ?? p.qty ?? '',
        price: p.price ?? p.unitPrice ?? '',
      })),
    }))
    .filter((inv) => inv.productList.length);
};

// 发票识别：建立一次 SSE 连接，逐个识别多个文件，全部完成后再断开连接。
// 每个文件一次 OCR 请求，逐条累积 choices[0].delta.content，收到 finishedEnd
// 后结束当前文件并继续下一个；全部完成返回与 urls 顺序一致的文本数组
const recognizeInvoices = ({ urls, chatId }) =>
  new Promise((resolve, reject) => {
    const clientId = String(chatId);
    const list = Array.isArray(urls) ? urls : [urls];
    const results = [];
    let es = null;
    let content = '';
    let index = 0;
    let settled = false;
    let watchdog = null;

    const closeConnect = () => {
      try {
        es && es.close();
      } catch (e) {
        /* ignore */
      }
      es = null;
      fetch(`${AI_BASE}/hlxj/sse/closeConnect?clientId=${clientId}`).catch(() => {});
    };

    const finish = (error, texts) => {
      if (settled) return;
      settled = true;
      clearTimeout(watchdog);
      closeConnect();
      if (error) reject(error);
      else resolve(texts);
    };

    const sendOcr = () => {
      if (settled) return;
      fetch(OCR_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: list[index],
          chatId: clientId,
          dataId: uuidV4(),
          chatType: 'flow_manager_ocr',
        }),
      })
        .then(async (res) => {
          const data = await res.json().catch(() => null);
          const msg = typeof data === 'string' ? data : (data && data.message) || '';
          if (!res.ok) {
            finish(new Error(msg || '发票识别请求失败'));
          } else if (data && typeof data === 'object' && data.success === false) {
            finish(new Error(msg || '发票识别失败'));
          }
        })
        .catch((err) => finish(err));
    };

    // 当前文件识别结束：保存结果，继续下一个或全部完成
    const finishCurrent = () => {
      results.push(content);
      content = '';
      index += 1;
      if (index >= list.length) {
        finish(null, results);
      } else {
        sendOcr();
      }
    };

    try {
      es = new EventSource(`${AI_BASE}/hlxj/sse/connected?clientId=${clientId}`);
    } catch (e) {
      finish(new Error('发票识别连接创建失败'));
      return;
    }

    es.onmessage = (event) => {
      if (event.data === 'finishedEnd') {
        finishCurrent();
        return;
      }
      let data = null;
      try {
        data = JSON.parse(event.data);
      } catch (e) {
        // 非 JSON 的流式文本直接累积
        content += event.data || '';
        return;
      }
      if (data && data.event === 'finishedEnd') {
        finishCurrent();
        return;
      }
      const chunk = data && data.choices && data.choices.length
        ? data.choices[0].delta && data.choices[0].delta.content
        : '';
      if (chunk) content += chunk;
    };

    es.onerror = () => {
      if (!settled) finish(new Error('发票识别连接异常，请重试'));
    };

    // 连接建立后延迟发起第一个识别请求
    setTimeout(() => {
      if (!settled) sendOcr();
    }, 200);

    // 超时按每个文件 60s 计算
    watchdog = setTimeout(() => {
      if (!settled) finish(new Error('发票识别超时，请重试'));
    }, list.length * 60000);
  });

// 把识别到的发票数据发给后端确认（按项目名称+规格匹配产品），返回校正后的数据
const selectProductByNameAndSpecify = (data) =>
  ajax
    .Post({
      url: `${MALL_BASE}/channelCmFlowManagerInfo/selectProductByNameAndSpecify`,
      data,
      timeout: 20000,
    })
    .then(({ data: res }) => {
      if (!res || res.success === false) {
        throw new Error((res && res.message) || '识别数据校验失败');
      }
      return res.data;
    });

// 手动保存上传流向（无发票）
const submitHandFlow = (data) =>
  ajax.Post({
    url: `${MALL_BASE}/channelCmFlowManagerInfo/addHandContent`,
    data,
    timeout: 20000,
  });

// 发票识别后保存流向
const submitOcrFlow = (data) =>
  ajax.Post({
    url: `${MALL_BASE}/channelCmFlowManagerInfo/addOcrContent`,
    data,
    timeout: 20000,
  });

export { getProductDict, getCustomers, uploadFiles, recognizeInvoices, parseInvoiceText, selectProductByNameAndSpecify, submitHandFlow, submitOcrFlow };
